import React, { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Snail, ShoppingCart, Smartphone, Boxes, TrendingDown } from 'lucide-react';
import { Reveal } from '@/components/site/Reveal';
import { SectionLabel } from '@/components/site/SectionLabel';
import { CTABand } from '@/components/site/CTABand';
import { FAQList } from '@/components/site/FAQ';
import {
  ServiceHero, ProblemsBlock, BuildBlock, ApproachBlock,
  OutcomesBlock, RelatedServices,
} from '@/components/site/ServicePageParts';

gsap.registerPlugin(ScrollTrigger);

const PROBLEMS = [
  { slug: 'slow-stores', icon: Snail, title: 'Slow stores', body: 'A second of delay is ~7% of conversion gone. We rebuild storefronts that paint sub-second on a midrange Indian phone, not just Lighthouse.' },
  { slug: 'abandoned-carts', icon: ShoppingCart, title: 'Abandoned carts', body: '70% is the global default. We bring it down with predictive cart, address autofill, OTP-less guest checkout, and honest shipping math.' },
  { slug: 'painful-mobile-checkout', icon: Smartphone, title: 'Painful mobile checkout', body: 'Most of your buyers are on a phone in a metro queue. We design checkout for the thumb—one screen, four fields, one tap.' },
  { slug: 'hard-catalogs', icon: Boxes, title: 'Hard-to-manage catalogs', body: 'Variants, bundles, GST slabs, regional pricing—built into the model, not bolted on later. Your merch team should not need a developer to launch.' },
  { slug: 'flat-conversion', icon: TrendingDown, title: 'Flat conversion', body: 'A nice theme can hold conversion flat for years. We CRO-test where it actually matters: PDP, cart, address, payment.' },
];

const BUILD = [
  { slug: 'shopify', tag: 'Shopify · Hydrogen', title: 'Shopify Hydrogen', body: 'Headless Shopify storefronts with editorial PDPs, lightning checkout, and merchandising tools your team enjoys.', accent: '#6C5CE7' },
  { slug: 'headless', tag: 'Headless · Composable', title: 'Headless commerce', body: 'Shopify, Medusa or commercetools backend + a hand-built React storefront. Best of both: ops in Shopify, brand in code.', accent: '#4B9FFF' },
  { slug: 'woocommerce', tag: 'WooCommerce · WP', title: 'WooCommerce', body: 'WP / Woo done properly: optimized themes, headless front via Next, payment + shipping wired to the Indian stack.', accent: '#6C5CE7' },
  { slug: 'custom', tag: 'Bespoke · Postgres', title: 'Custom storefronts', body: 'When SKUs hit the millions or the model is non-standard (rentals, subscriptions, marketplaces), we build from the data up.', accent: '#4B9FFF' },
  { slug: 'b2b', tag: 'B2B · Portal', title: 'B2B portals', body: 'Quote, approval, ledger, AR, repeat-order. Built for dealers, distributors and key-account buyers—not retail.', accent: '#4B9FFF' },
  { slug: 'subscription', tag: 'Subscription · Repeat', title: 'Subscription commerce', body: 'Replenishment, gifting, pause / skip, smart card retries. Built for LTV first—AOV is a bonus.', accent: '#6C5CE7' },
];

const APPROACH = [
  { slug: 'strategy', step: '01', title: 'Strategy', body: 'A two-week diagnosis: traffic, funnel, AOV, repeat. We agree on the three numbers we will move and the three we won&apos;t touch.' },
  { slug: 'ux-cro', step: '02', title: 'UX & CRO', body: 'PDP layout, cart logic, checkout fields, shipping copy. We rewrite the parts that move the needle, not the whole site.' },
  { slug: 'design', step: '03', title: 'Design', body: 'Editorial design for the brand surface, ruthless utility for the funnel. Tokens, components, motion notes, real copy.' },
  { slug: 'build', step: '04', title: 'Build', body: 'Hydrogen / Next + Tailwind. CMS for content, Shopify / Medusa for commerce. CI + previews on every PR.' },
  { slug: 'integrations', step: '05', title: 'Payments / shipping / tax', body: 'Razorpay, Stripe, UPI, COD reconciliation, Shiprocket / Delhivery, GST + e-invoice. The boring stuff, done right.' },
  { slug: 'testing', step: '06', title: 'Testing', body: 'Lighthouse, axe, real-device, payment sandbox cycles, refund flows. Then a soft-launch with a slice of traffic.' },
  { slug: 'launch', step: '07', title: 'Launch', body: 'A full migration runbook—redirects, schema, indexability, observability. SEO doesn&apos;t fall off a cliff because we replatformed.' },
  { slug: 'optimization', step: '08', title: 'Optimization', body: 'Monthly CRO + speed + content reviews. The site that&apos;s 10% better in six months is the whole story.' },
];

const PLATFORMS = [
  'Shopify', 'Shopify Hydrogen', 'Medusa', 'commercetools', 'WooCommerce',
  'Razorpay', 'Stripe', 'UPI', 'Cashfree', 'Shiprocket', 'Delhivery',
  'Klaviyo', 'Mailmodo', 'PostHog', 'Algolia', 'Searchspring', 'Tax (GST)',
];

const PILLARS = [
  { k: 'CRO', v: 'Hypothesis-driven testing on PDP, cart and checkout. We publish wins and losses both.' },
  { k: 'Speed', v: 'Performance budgets per surface. ≤ 1s LCP on a midrange Indian phone, not just a desktop benchmark.' },
  { k: 'Checkout', v: 'OTP-less guest checkout, address autofill, predictive cart. Four fields, one screen, one tap.' },
  { k: 'Security', v: 'PCI-friendly architecture, secrets management, audit trails. We never touch raw card data.' },
];

const FAQ = [
  { q: 'Shopify or headless?', a: 'Shopify when ops + speed-to-launch matter more than brand surface. Headless when brand and motion are the differentiator. Most of our work in 2025 is Shopify Hydrogen—best of both.' },
  { q: 'Will the migration tank SEO?', a: 'It doesn&apos;t have to. We map every URL, set 301s, mirror schema and indexability, and watch Search Console daily for three weeks post-launch.' },
  { q: 'Do you support Indian payments + GST?', a: 'Yes—Razorpay, Cashfree, UPI, COD reconciliation, e-invoicing. Multi-state GST handling on day one.' },
  { q: 'Can you handle replatforming from Magento / Woo?', a: 'Yes. We&apos;ve done it twice this year. Catalog, customers, orders, redirects—all migrate. We keep the old store live in mirror-mode for the first 48 hours of cutover.' },
  { q: 'Do you run paid + lifecycle as well?', a: 'No—we focus on the platform. We work alongside your media + lifecycle partners, and instrument the site to feed them clean data.' },
  { q: 'How fast can we launch?', a: 'A focused Shopify Hydrogen storefront: 10–14 weeks. A custom marketplace: 16–24 weeks. A landing-store for a launch: 3 weeks.' },
];

const OUTCOMES = [
  { kpi: 42, prefix: '+', suffix: '%', label: 'median conversion lift' },
  { kpi: 32, prefix: '+', suffix: '%', label: 'AOV uplift in 90 days' },
  { kpi: 0.9, suffix: 's', label: 'avg. LCP on 4G mid-tier' },
  { kpi: 3.4, prefix: '×', suffix: '', label: 'monthly revenue post-launch' },
];

/* ----- Signature: store before/after with animated revenue counters ----- */
function StoreBeforeAfter() {
  const root = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);
  const [stats, setStats] = useState({ rev: 0, cvr: 0, aov: 0 });
  const statsRef = useRef({ rev: 0, cvr: 0, aov: 0 });

  useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current || !root.current) return;
      const rect = root.current.getBoundingClientRect();
      const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
      setPos(Math.max(0, Math.min(100, (x / rect.width) * 100)));
    };
    const onUp = () => { dragging.current = false; document.body.style.userSelect = ''; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, []);

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !root.current) return;
    const ctx = gsap.context(() => {
      gsap.to(statsRef.current, {
        rev: 2840000, cvr: 4.2, aov: 1620,
        ease: 'power2.out',
        scrollTrigger: { trigger: root.current, start: 'top 75%', end: 'bottom 30%', scrub: 0.6 },
        onUpdate: () => setStats({ ...statsRef.current }),
      });
    }, root);
    return () => ctx.revert();
  }, []);

  const startDrag = (e) => {
    dragging.current = true;
    document.body.style.userSelect = 'none';
    if (root.current) {
      const rect = root.current.getBoundingClientRect();
      const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
      setPos(Math.max(0, Math.min(100, (x / rect.width) * 100)));
    }
  };
  const onKey = (e) => {
    if (e.key === 'ArrowLeft') setPos((p) => Math.max(0, p - 2));
    if (e.key === 'ArrowRight') setPos((p) => Math.min(100, p + 2));
  };

  const inr = (n) => `₹${Math.round(n).toLocaleString('en-IN')}`;

  return (
    <div className="space-y-6">
      <div
        ref={root}
        data-testid="ec-before-after"
        onMouseDown={startDrag}
        onTouchStart={startDrag}
        className="relative aspect-[16/10] w-full rounded-[20px] overflow-hidden border border-white/[0.12] bg-ink-surface select-none cursor-ew-resize"
      >
        {/* After (full behind) */}
        <StoreMock variant="after" />
        {/* Before (clipped) */}
        <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
          <StoreMock variant="before" />
        </div>

        <div className="pointer-events-none absolute top-4 left-4 text-[10px] uppercase tracking-[0.28em] text-white/85 bg-black/40 backdrop-blur-sm border border-white/15 rounded-full px-3 py-1.5">
          Before
        </div>
        <div className="pointer-events-none absolute top-4 right-4 text-[10px] uppercase tracking-[0.28em] text-white/85 bg-black/40 backdrop-blur-sm border border-white/15 rounded-full px-3 py-1.5">
          After
        </div>

        <div className="absolute top-0 bottom-0 w-px bg-white/80" style={{ left: `${pos}%` }}>
          <button
            type="button"
            data-testid="ec-before-after-handle"
            aria-label="Drag to compare"
            tabIndex={0}
            onKeyDown={onKey}
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-12 w-12 rounded-full bg-white text-[#0A0A0F] shadow-[0_8px_30px_-8px_rgba(0,0,0,0.6)] flex items-center justify-center hover:scale-105 transition-transform"
          >
            <span className="-mr-0.5">‹</span><span>›</span>
          </button>
        </div>
        <div className="pointer-events-none absolute bottom-4 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.28em] text-white/60">
          Drag to compare
        </div>
      </div>

      {/* Animated revenue stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { l: 'Monthly revenue · post-launch', v: inr(stats.rev), c: '#6C5CE7' },
          { l: 'Site-wide conversion', v: `${stats.cvr.toFixed(1)}%`, c: '#4B9FFF' },
          { l: 'AOV', v: inr(stats.aov), c: '#6C5CE7' },
        ].map((s) => (
          <div key={s.l} className="rounded-[16px] border border-white/[0.12] bg-ink-bg p-5">
            <div className="text-[10px] uppercase tracking-[0.22em] text-ink-muted">{s.l}</div>
            <div className="mt-3 text-2xl md:text-3xl font-bold tracking-tight tabular-nums" style={{ color: s.c }}>{s.v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StoreMock({ variant }) {
  if (variant === 'before') {
    return (
      <div className="absolute inset-0 bg-[#F6F2EB] text-[#222]">
        <div className="h-12 border-b border-black/10 px-6 flex items-center justify-between bg-[#E9E0CC]">
          <div className="text-[11px] uppercase tracking-widest">Mithila Sarees — Welcome!!</div>
          <div className="hidden md:flex gap-4 text-[10px] uppercase tracking-widest text-black/60">
            <span>Home</span><span>Shop</span><span>Sale</span><span>Cart (0)</span>
          </div>
        </div>
        <div className="p-6 grid grid-cols-12 gap-4 h-[calc(100%-3rem)]">
          <div className="col-span-7">
            <div className="text-[10px] uppercase tracking-widest text-orange-700">FLAT 60% OFF ★ HURRY ★</div>
            <div className="mt-3 font-serif text-2xl md:text-3xl leading-snug">
              <span className="text-orange-700">SALE!!</span> Handloom sarees from ₹999 onwards
            </div>
            <p className="mt-3 text-[11px] text-black/70 leading-relaxed">Free shipping on prepaid orders above ₹1,499. Hurry, limited stock. T&C apply.</p>
            <div className="mt-4 grid grid-cols-3 gap-2">
              {[1,2,3,4,5,6].map(i => (
                <div key={i} className="aspect-square border border-black/15 bg-[length:8px_8px] bg-[linear-gradient(135deg,rgba(0,0,0,0.05)_25%,transparent_25%,transparent_50%,rgba(0,0,0,0.05)_50%,rgba(0,0,0,0.05)_75%,transparent_75%)] flex items-center justify-center">
                  <div className="text-[9px] text-black/40 uppercase">Image {i}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="col-span-5 border border-black/10 p-4 bg-white">
            <div className="text-[11px] uppercase tracking-wider">Subscribe to our news!!</div>
            <div className="mt-2 h-7 border border-black/30" />
            <button className="mt-3 px-3 py-1.5 bg-orange-700 text-white text-[11px] uppercase">Submit</button>
          </div>
        </div>
      </div>
    );
  }
  // After — editorial
  return (
    <div className="absolute inset-0 bg-[#0A0A0F] text-white overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
      <div
        className="absolute -right-32 -top-32 h-[420px] w-[420px] rounded-full blur-[120px] pointer-events-none"
        style={{ background: 'rgba(108,92,231,0.32)' }}
      />
      <div className="h-12 px-6 flex items-center justify-between border-b border-white/[0.08]">
        <div className="text-[12px] font-semibold tracking-tight">Mithila <span className="text-white/50">— since 1894</span></div>
        <div className="hidden md:flex gap-5 text-[11px] text-white/70 items-center">
          <span>Looms</span><span>Stories</span><span>Trousseau</span>
          <span className="px-3 py-1.5 rounded-full bg-violet text-white text-[11px]">Cart · 1</span>
        </div>
      </div>
      <div className="p-6 grid grid-cols-12 gap-5 h-[calc(100%-3rem)]">
        <div className="col-span-7">
          <div className="text-[10px] uppercase tracking-[0.28em] text-white/50">— Loom 27 · Madhubani</div>
          <div className="mt-3 text-2xl md:text-4xl font-extrabold tracking-tight leading-[1.04]">
            The <span className="italic text-violet">cotton sky</span>, woven by hand for forty-two hours.
          </div>
          <div className="mt-3 text-[12px] text-white/70 leading-relaxed max-w-md">
            One saree. Two weavers. Forty-two hours. Limited to 17 pieces — provenance card in every box.
          </div>
          <div className="mt-4 flex gap-2">
            <span className="px-3 py-2 rounded-full bg-violet text-[11px]">Add to bag · ₹18,400</span>
            <span className="px-3 py-2 rounded-full border border-white/15 text-[11px]">Save</span>
          </div>
        </div>
        <div className="col-span-5 grid grid-cols-2 gap-2">
          {[0,1,2,3].map(i => (
            <div key={i} className="rounded-lg border border-white/[0.12]"
              style={{ background: `radial-gradient(circle at ${20+i*20}% 30%, rgba(108,92,231,0.35), transparent 55%), #0E0E15` }} />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function EcommerceWebsiteDevelopment() {
  const platRef = useRef(null);
  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !platRef.current) return;
    const ctx = gsap.context(() => {
      gsap.to('.ec-marquee-inner', { xPercent: -50, ease: 'none', duration: 28, repeat: -1 });
    }, platRef);
    return () => ctx.revert();
  }, []);

  return (
    <div data-testid="ecommerce-website-development-page">
      <ServiceHero
        serviceNum="05"
        serviceTitle="Ecommerce Website Development"
        headlineText="Storefronts built to sell, not just to look good."
        accent="sell,"
        description="Headless Shopify, Medusa, Woo or a custom storefront engineered for conversion, AOV and repeat purchase. Built for the way Indian buyers shop — phone, COD, vernacular, share-to-cart."
        secondaryCta={{ label: 'See store work', to: '/work' }}
        testIdPrefix="ec"
      />

      <ProblemsBlock
        heading="Five things keeping your store flat."
        accentWord="flat."
        intro="Pretty doesn’t pay. We focus on the five places where store performance turns into revenue."
        items={PROBLEMS}
        testIdPrefix="ec"
      />

      <BuildBlock
        heading="Six commerce surfaces."
        accentWord="commerce surfaces."
        intro="Pick the right shape for the catalog. We help you choose in week one."
        items={BUILD}
        testIdPrefix="ec"
      />

      <ApproachBlock
        heading="Eight stages. One funnel."
        accentWord="One funnel."
        intro="From the audit to the optimisation cadence, we own the whole funnel surface—not just the part that looks like a design file."
        steps={APPROACH}
        testIdPrefix="ec"
      />

      {/* Platforms marquee + pillars */}
      <section
        ref={platRef}
        data-testid="ec-platforms"
        className="relative py-24 md:py-28 border-y border-white/10 bg-ink-surface/30 overflow-hidden"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 mb-12 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <SectionLabel num="05">Platforms & stack</SectionLabel>
            <h2 className="mt-6 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05] max-w-2xl text-balance">
              The stack we&apos;d <span className="italic text-electric">trust</span> with your Black Friday.
            </h2>
          </div>
          <div className="md:col-span-4 md:col-start-9">
            <Reveal>
              <p className="text-ink-muted leading-relaxed max-w-md">
                We are platform-flexible. The right answer is the one your team can run for the next three years without us in the room.
              </p>
            </Reveal>
          </div>
        </div>
        <div className="relative">
          <div className="absolute left-0 top-0 bottom-0 w-32 z-10 bg-gradient-to-r from-ink-bg to-transparent pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-32 z-10 bg-gradient-to-l from-ink-bg to-transparent pointer-events-none" />
          <div className="flex whitespace-nowrap ec-marquee-inner gap-6 will-change-transform">
            {[...PLATFORMS, ...PLATFORMS].map((t, i) => (
              <div key={i} className="shrink-0 inline-flex items-center gap-3 px-6 py-3.5 rounded-full border border-white/[0.12] bg-ink-bg text-sm tracking-tight">
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: i % 2 === 0 ? '#6C5CE7' : '#4B9FFF' }} />
                {t}
              </div>
            ))}
          </div>
        </div>
        {/* Pillars row */}
        <div className="max-w-7xl mx-auto px-6 md:px-12 mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PILLARS.map((p, i) => (
            <Reveal key={p.k} delay={i * 80}>
              <div className="h-full rounded-[16px] border border-white/[0.12] bg-ink-bg p-6">
                <div className="text-[11px] uppercase tracking-[0.28em] text-ink-muted">— Pillar 0{i+1}</div>
                <div className="mt-4 text-xl font-semibold tracking-tight">{p.k}</div>
                <p className="mt-3 text-ink-muted leading-relaxed text-sm">{p.v}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Signature: store before/after */}
      <section data-testid="ec-signature" className="relative py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12 items-end">
            <div className="md:col-span-7">
              <SectionLabel num="06">Recent · Mithila Textiles</SectionLabel>
              <h2 className="mt-6 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05] max-w-3xl text-balance">
                <span className="italic text-violet">Drag the handle.</span> Watch the revenue panels tick up.
              </h2>
            </div>
            <div className="md:col-span-5 md:pl-6 md:border-l border-white/10">
              <Reveal>
                <p className="text-ink-muted leading-relaxed max-w-md">
                  A 4-generation weaving house in Madhubani. We rebuilt the storefront around the loom, the weaver and the saree—instead of yet another "SALE 60% OFF" banner.
                </p>
              </Reveal>
              <ul className="mt-6 space-y-2 text-sm">
                <li className="flex gap-3"><span className="text-violet">▸</span><span><span className="text-white">Monthly revenue</span> <span className="text-ink-muted">— ₹8.3L → ₹28.4L</span></span></li>
                <li className="flex gap-3"><span className="text-violet">▸</span><span><span className="text-white">CVR</span> <span className="text-ink-muted">— 0.9% → 4.2%</span></span></li>
                <li className="flex gap-3"><span className="text-violet">▸</span><span><span className="text-white">AOV</span> <span className="text-ink-muted">— ₹980 → ₹1,620</span></span></li>
              </ul>
            </div>
          </div>
          <Reveal>
            <StoreBeforeAfter />
          </Reveal>
        </div>
      </section>

      <OutcomesBlock
        heading="Numbers we’d sign a contract against."
        accentWord="contract"
        intro="Medians across the last 9 ecommerce engagements (2023–25)."
        values={OUTCOMES}
        testIdPrefix="ec"
      />

      <section data-testid="ec-faq" className="relative py-24 md:py-32 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <SectionLabel num="08">FAQ</SectionLabel>
            <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight leading-[1.1]">
              Things we&apos;re asked <span className="italic text-violet">before</span> the call.
            </h2>
          </div>
          <div className="md:col-span-8">
            <FAQList items={FAQ} testIdPrefix="ec-faq" />
          </div>
        </div>
      </section>

      <RelatedServices exclude="ecommerce-website-development" testIdPrefix="ec" />

      <CTABand
        eyebrow="Store ready for the next chapter?"
        headline="Let&apos;s ship a store that actually sells."
        sub="A 30-minute WhatsApp call with a partner. Bring last month&apos;s funnel screenshot from GA / PostHog."
      />
    </div>
  );
}
